import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import SettlementLines from "@/components/finance/SettlementLines";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { ledgerService } from "@/services/ledgerService";
import { receivableService } from "@/services/receivableService";
import { handleApiError } from "@/services/api";
import type { BuyerLedger } from "@/types/models";
import { useIsOwner } from "@/hooks/useRole";
import { formatApplyResult } from "@/lib/utils";

const methodLabels: Record<string, string> = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

const statusColor: Record<string, "success" | "warning" | "error"> = {
  paid: "success",
  partial: "warning",
  unpaid: "error",
};

const saleTypeLabels: Record<string, string> = {
  eggs: "Eggs",
  birds: "Birds",
  manure: "Manure",
  bags: "Bags",
};

function formatCurrency(n: number) {
  return "\u20B9" + Math.abs(n).toLocaleString("en-IN");
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function daysUntil(dateStr: string) {
  const diff = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff === 0) return "Today";
  return `${diff}d`;
}

export default function BuyerLedgerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Recording money is OWNER-only in the API (payments, advances, apply
  // credit). Gating on "not a viewer" showed a manager or supervisor the
  // forms and let them fill one in, then refused the save with a 403.
  const canRecordMoney = useIsOwner();
  const [confirmDeleteReceipt, setConfirmDeleteReceipt] = useState<number | null>(null);
  // ?highlight=<receiptId> — set when arriving from a bank-ledger match link,
  // so the operator lands on the exact receipt/advance that match created.
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get("highlight")) || null;
  // One-click "apply this buyer's advance credit across all open bills" is gated
  // behind a per-farm feature flag (owner opt-in in Farm Settings).

  const [ledger, setLedger] = useState<BuyerLedger | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Apply-credit confirm dialog.
  const [showApplyCredit, setShowApplyCredit] = useState(false);
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [applyResult, setApplyResult] = useState<string>("");

  const fetchLedger = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const data = await ledgerService.getBuyerLedger(parseInt(id));
      setLedger(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // After the ledger renders, scroll the highlighted (bank-linked) row into
  // view. Runs once entries are present; harmless when there's no highlight.
  useEffect(() => {
    if (highlightId == null || !ledger) return;
    const row = document.querySelector(".ledger-row-highlight");
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId, ledger]);

  const handleApplyCredit = async () => {
    if (!id) return;
    setApplyingCredit(true);
    setApplyResult("");
    try {
      const res = await receivableService.applyCredit(parseInt(id));
      setApplyResult(formatApplyResult(res));
      await fetchLedger();
    } catch (err) {
      setApplyResult(handleApiError(err));
    } finally {
      setApplyingCredit(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageMeta title="Buyer Ledger | Poultry Book" description="Buyer transaction history" />
        <PageBreadcrumb pageTitle="Buyer Ledger" />
        <div className="py-10 text-center text-gray-500 dark:text-gray-400">Loading...</div>
      </>
    );
  }

  if (error && !ledger) {
    return (
      <>
        <PageMeta title="Buyer Ledger | Poultry Book" description="Buyer transaction history" />
        <PageBreadcrumb pageTitle="Buyer Ledger" />
        <div className="py-10 text-center text-error-500">{error}</div>
      </>
    );
  }

  if (!ledger) return null;

  const { buyer, summary } = ledger;
  const upcoming_dues = ledger.upcoming_dues ?? [];
  const entries = ledger.entries ?? [];

  return (
    <>
      <PageMeta title={`${buyer.name} Ledger | Poultry Book`} description="Buyer transaction history" />
      <PageBreadcrumb pageTitle={buyer.name} />

      {/* Back link */}
      <button
        onClick={() => navigate("/receivables")}
        className="mb-4 flex items-center gap-1 text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
      >
        &larr; Back to Accounts Receivable
      </button>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">{buyer.name}</h2>
          {buyer.phone && <p className="text-sm text-gray-500 dark:text-gray-400">{buyer.phone}</p>}
        </div>
        {buyer.credit_days != null && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Credit: {buyer.credit_days} days
          </span>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-900/20 dark:text-error-400">{error}</div>}

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Invoiced</p>
          <p className="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">{formatCurrency(summary.total_invoiced)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Received</p>
          <p className="mt-1 text-lg font-semibold text-gray-800 dark:text-white/90">{formatCurrency(summary.total_received)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Net Balance</p>
          <p className={`mt-1 text-lg font-semibold ${summary.net_balance > 0 ? "text-error-600 dark:text-error-400" : summary.net_balance < 0 ? "text-success-600 dark:text-success-400" : "text-gray-800 dark:text-white/90"}`}>
            {formatCurrency(summary.net_balance)}
            {summary.net_balance > 0 && <span className="ml-1 text-xs font-normal">(They Owe)</span>}
            {summary.net_balance < 0 && <span className="ml-1 text-xs font-normal">(Credit)</span>}
          </p>
          {/* Plan §11.8 — see SupplierLedgerPage for the rationale. */}
          {summary.available_credit > 0 && (
            <p className="mt-1 text-xs text-brand-600 dark:text-brand-400" data-testid="available-credit-line">
              {"₹"}{summary.available_credit.toLocaleString("en-IN")} available credit
              <span className="text-gray-400">
                {" "}from {summary.available_credit_count} earlier receipt{summary.available_credit_count === 1 ? "" : "s"}
              </span>
            </p>
          )}
          {/* One-click FIFO settle of this buyer's credit across all open bills.
              Owner-only, gated behind the per-farm feature flag. Shown when there
              is credit AND open bills to apply it to — gross open bills =
              net_balance + available_credit (net already subtracts the credit,
              so it goes negative once credit exceeds debt while bills remain). */}
          {/* Kept visible (no flag): credit held from before Record Payment
              settled oldest-first has no other way onto bills already open. */}
          {canRecordMoney && summary.available_credit > 0 && summary.net_balance + summary.available_credit > 0 && (
            <button
              type="button"
              onClick={() => { setApplyResult(""); setShowApplyCredit(true); }}
              className="mt-2 rounded-md bg-gray-900 dark:bg-white px-2.5 py-1 text-xs font-medium text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100"
            >
              Apply credit to bills
            </button>
          )}
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">Due Soon</p>
          <p className="mt-1 text-lg font-semibold text-orange-600 dark:text-orange-400">
            {summary.due_soon_count > 0 ? (
              <>{formatCurrency(summary.due_soon_amount)} <span className="text-xs font-normal">({summary.due_soon_count} invoices)</span></>
            ) : summary.overdue_count > 0 ? (
              <>{formatCurrency(summary.overdue_amount)} <span className="text-xs font-normal text-error-600 dark:text-error-400">({summary.overdue_count} overdue)</span></>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </p>
        </div>
      </div>

      {/* Upcoming Dues */}
      {upcoming_dues.length > 0 && (
        <div className="mb-6">
          <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Upcoming Dues</h3>
          <div className="space-y-2">
            {upcoming_dues.map((r) => {
              const outstanding = r.total_amount - r.received_amount;
              const isOverdue = r.due_date ? new Date(r.due_date) < new Date() : false;
              return (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 list-row px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-800 dark:text-white/90">
                      {r.invoice_number || `Invoice #${r.id}`}
                    </span>
                    <span className="text-sm text-gray-500">{formatCurrency(outstanding)} outstanding</span>
                    {r.sale_type && (
                      <Badge color="light">{saleTypeLabels[r.sale_type] ?? r.sale_type}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {r.due_date && (
                      <span className={`text-sm ${isOverdue ? "font-medium text-error-600 dark:text-error-400" : "text-gray-500 dark:text-gray-400"}`}>
                        Due: {formatDate(r.due_date)} ({daysUntil(r.due_date)})
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ledger Table */}
      <div className="overflow-hidden card">
        <h3 className="border-b border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 dark:border-white/[0.05] dark:text-gray-300">
          Ledger
        </h3>
        {entries.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">No transactions yet</div>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell isHeader>Date</TableCell>
                  <TableCell isHeader>Particulars</TableCell>
                  <TableCell isHeader>Debit</TableCell>
                  <TableCell isHeader>Credit</TableCell>
                  <TableCell isHeader>Balance</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e, i) => {
                  // A receipt/advance row matches the ?highlight target (a receipt
                  // id); invoices are excluded since their ids share the sequence.
                  const isHighlight =
                    highlightId != null && e.id === highlightId && (e.type === "receipt" || e.type === "advance");
                  return (
                  <TableRow
                    key={`${e.type}-${e.id}-${i}`}
                    className={isHighlight ? "ledger-row-highlight bg-brand-50 dark:bg-brand-500/10" : undefined}
                  >
                    <TableCell className="whitespace-nowrap text-sm">{formatDate(e.date)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {e.type === "invoice" && (
                          <>
                            <span className="font-medium">Invoice</span>
                            {e.invoice_number && <span className="text-gray-500">: {e.invoice_number}</span>}
                            {e.sale_type && (
                              <span className="ml-1 text-gray-400">({saleTypeLabels[e.sale_type] ?? e.sale_type})</span>
                            )}
                            {e.status && (
                              <> <Badge color={statusColor[e.status] ?? "light"}>
                                {e.status}
                              </Badge></>
                            )}
                          </>
                        )}
                        {e.type === "receipt" && (
                          <>
                            <span className="font-medium">Receipt</span>
                            {e.method && <span className="text-gray-500"> - {methodLabels[e.method] ?? e.method}</span>}
                            {e.reference && <span className="text-gray-400"> ({e.reference})</span>}
                            {e.parent_receipt_id != null && (
                              <span
                                className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                title="Settled by leftover credit carried forward from an earlier overpayment."
                                data-testid={`leftover-credit-badge-${e.id}`}
                              >
                                leftover credit
                              </span>
                            )}
                          </>
                        )}
                        {e.type === "advance" && (
                          <>
                            <span className="font-medium">Receipt</span>
                            {e.method && <span className="text-gray-500"> - {methodLabels[e.method] ?? e.method}</span>}
                            {e.reference && <span className="text-gray-400"> ({e.reference})</span>}
                            {e.source_type === "bank_statement_transaction" && (
                              <span
                                className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                title="Tagged from bank statement — to delete or correct, use Unmatch on the bank ledger row."
                                data-testid={`from-bank-badge-${e.id}`}
                              >
                                from bank
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {e.notes && <p className="mt-0.5 text-xs text-gray-400">{e.notes}</p>}
                      {e.type === "advance" && <SettlementLines settled={e.settled} held={e.held} docLabel="Invoice" />}
                      {e.type === "advance" && canRecordMoney && !e.source_type && (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteReceipt(e.id)}
                          className="mt-1 text-xs font-medium text-error-500 hover:text-error-600 dark:text-error-400"
                          data-testid={`delete-receipt-${e.id}`}
                        >
                          Delete
                        </button>
                      )}
                      {e.type === "receipt" && e.parent_receipt_id != null && (
                        <p
                          className="mt-0.5 text-xs text-brand-600 dark:text-brand-400"
                          data-testid={`leftover-credit-line-${e.id}`}
                        >
                          From receipt #{e.parent_receipt_id} · received {formatDate(e.date)}
                          {e.applied_at && formatDate(e.applied_at) !== formatDate(e.date) && (
                            <> · applied {formatDate(e.applied_at)}</>
                          )}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className={e.debit > 0 ? "font-medium text-error-600 dark:text-error-400" : ""}>
                      {e.debit > 0 ? formatCurrency(e.debit) : ""}
                    </TableCell>
                    <TableCell className={e.credit > 0 ? "font-medium text-success-600 dark:text-success-400" : ""}>
                      {e.credit > 0 ? formatCurrency(e.credit) : ""}
                    </TableCell>
                    <TableCell className={`font-medium ${e.balance > 0 ? "text-error-600 dark:text-error-400" : e.balance < 0 ? "text-success-600 dark:text-success-400" : ""}`}>
                      {formatCurrency(e.balance)}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDeleteReceipt !== null}
        title="Delete receipt"
        message="The invoices it settled go back to unpaid (or part-paid), and any credit it left is removed."
        onClose={() => setConfirmDeleteReceipt(null)}
        onConfirm={async () => {
          if (confirmDeleteReceipt === null) return;
          try {
            await receivableService.deleteReceipt(confirmDeleteReceipt);
            setConfirmDeleteReceipt(null);
            fetchLedger();
          } catch (err) {
            setConfirmDeleteReceipt(null);
            setError(handleApiError(err));
          }
        }}
      />

      {/* Apply-credit confirm — preview then confirm (not silent). */}
      <Modal isOpen={showApplyCredit} onClose={() => setShowApplyCredit(false)} className="max-w-md">
        <div className="p-6">
          <h3 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">Apply credit to bills</h3>
          {ledger && (
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
              Apply <span className="font-semibold">{formatCurrency(ledger.summary.available_credit)}</span> of{" "}
              {ledger.buyer.name}&rsquo;s advance credit across their open bills, <span className="font-medium">oldest first</span>.
              Up to <span className="font-semibold">{formatCurrency(Math.min(ledger.summary.available_credit, ledger.summary.net_balance + ledger.summary.available_credit))}</span> will be settled; any excess stays as credit.
            </p>
          )}
          {applyResult && (
            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-white/[0.06] dark:text-gray-200" data-testid="apply-credit-result">
              {applyResult}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowApplyCredit(false)}>
              {applyResult ? "Close" : "Cancel"}
            </Button>
            {!applyResult && (
              <Button size="sm" onClick={handleApplyCredit} disabled={applyingCredit}>
                {applyingCredit ? "Applying…" : "Apply credit"}
              </Button>
            )}
          </div>
        </div>
      </Modal>

    </>
  );
}
